import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { formatHyphenatedDate, getCurrentJstDayjs } from '@/utils/date';
import { sendGAWabunkaSelectEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectEvent';
import { sendGAWabunkaViewEventListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/viewEventList';
import type { WabunkaReservationFormStep1 } from '@/wabunka-src/features/Experience';
import { useListExperienceEventsByDate } from '@/wabunka-src/features/Experience/composables/query/useListExperienceEventsByDateQuery';
import {
  buildWabunkaCandidateEventOptionViewModel,
  createDefaultWabunkaExperienceEventViewModel,
  type WabunkaCandidateEventOption,
  type WabunkaExperienceEventViewModel,
} from '@/wabunka-src/features/Experience/models/experienceEvent';

interface UseExperienceEventSelectionProps {
  experience: WabunkaExperienceViewModel;
  experienceEvent: WabunkaExperienceEventViewModel;
  reservationStep1Form: WabunkaReservationFormStep1;
  experienceId: ComputedRef<string>;
  startDateFrom: Ref<string>;
  startDateTo: Ref<string>;
  onChangeExperienceEventId: (experienceEventId: string) => void;
  initialUpdateFromExperienceEvent: (experienceEvent: WabunkaExperienceEventViewModel) => void;
}

/**
 * 体験イベント選択肢管理用Composable
 */
export const useExperienceEventSelection = ({
  experience,
  experienceEvent,
  reservationStep1Form,
  experienceId,
  startDateFrom,
  startDateTo,
  onChangeExperienceEventId,
  initialUpdateFromExperienceEvent,
}: UseExperienceEventSelectionProps) => {
  const candidateEvents = ref<WabunkaExperienceEventViewModel[]>([]);
  const candidateEventOptions = ref<WabunkaCandidateEventOption[]>([]);
  const canReserveEventOptions = ref<WabunkaCandidateEventOption[]>([]);

  const { experienceEventDict, isLoading } = useListExperienceEventsByDate(experienceId, { startDateFrom, startDateTo }, { accumulate: true, customKey: 'modal' });

  /**
   * 必要に応じてデフォルトの体験イベントIDを設定する関数。
   * この関数は、利用可能なイベントオプションが存在し、かつ現在イベントが選択されていない場合に
   * 自動的に最初の利用可能なイベントオプションを選択する。
   *
   * 以下2つのシナリオで使用：
   * 1. コンポーネントの初期マウント時
   * 2. 選択された日付が変更された時、または体験イベント辞書が更新された時
   */
  const setDefaultExperienceEventIdIfNeeded = () => {
    if (canReserveEventOptions.value.length > 0 && reservationStep1Form.experienceEventId !== canReserveEventOptions.value[0].value && reservationStep1Form.experienceEventId === '') {
      // 一番上のイベントを選択
      onChangeExperienceEventId(canReserveEventOptions.value[0].value);
    }
  };

  /**
   * 日付に基づいて体験イベントの候補を更新する関数
   */
  const updateCandidateEvents = (date: string) => {
    candidateEvents.value = experienceEventDict.value[date] ?? [];
    candidateEventOptions.value = buildWabunkaCandidateEventOptionViewModel(candidateEvents.value);
    canReserveEventOptions.value = candidateEventOptions.value.filter((candidateEventOption) => !candidateEventOption.disabled);

    setDefaultExperienceEventIdIfNeeded();
    sendGAWabunkaViewEventListEvent(experience, candidateEvents.value, date);
  };

  // 初期ロード時に candidateEvents を設定
  onMounted(async () => {
    const date = formatHyphenatedDate(reservationStep1Form.selectedDate ?? getCurrentJstDayjs());
    updateCandidateEvents(date);
    // 体験イベントが選択されていない場合は、一番上のイベントを選択
    if (!canReserveEventOptions.value.some((option) => option.value === experienceEvent.id)) {
      const selectedEvent = candidateEvents.value.find((event) => event.id === canReserveEventOptions.value[0]?.value);
      if (selectedEvent != null) {
        initialUpdateFromExperienceEvent(selectedEvent);
      }
    }
  });

  // これより先の予約導線から戻ってきた時、または日付が選択された時
  watch(
    [() => reservationStep1Form.selectedDate, () => experienceEventDict],
    async () => {
      if (reservationStep1Form.selectedDate == null) {
        return;
      }
      const date = formatHyphenatedDate(reservationStep1Form.selectedDate);
      updateCandidateEvents(date);
    },
    { immediate: true, deep: true },
  );

  // 時間が選択された＝体験イベントが選択された
  watch(
    () => reservationStep1Form.experienceEventId,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        const selectedEvent = candidateEvents.value.find((event) => event.id === newValue);
        if (selectedEvent == null) {
          initialUpdateFromExperienceEvent(createDefaultWabunkaExperienceEventViewModel());
          return;
        }
        initialUpdateFromExperienceEvent(selectedEvent);
        sendGAWabunkaSelectEvent(selectedEvent, reservationStep1Form.selectedDate ? formatHyphenatedDate(reservationStep1Form.selectedDate) : '');
      }
    },
  );

  return {
    experienceEventDict,
    isLoading,
    candidateEvents,
    canReserveEventOptions,
  };
};
